import React, { useCallback, useMemo } from "react";
import { CameraButton, ControlBar, DevicesButton, MicrophoneButton, usePropsFor, CallingTheme, useTheme } from "@azure/communication-react";
import { controlBarButtonStyles, controlBarStyles } from "../styles/ControlBarButton.styles";
import { devicePermissionSelector, usePcpLiveLessonContext } from "../store";
import { Theme } from "@fluentui/react";
import { MicrophoneIconOn, MicrophoneIconOff, CameraIconOn, CameraIconOff } from "./Icons";

export interface LocalPreviewControlBarProps {
    mobileView: boolean;
}

export const LocalPreviewControlBar = (props: LocalPreviewControlBarProps): JSX.Element => {
    const cameraButtonProps = usePropsFor(CameraButton);
    const devicesButtonProps = usePropsFor(DevicesButton);

    const isLocalMicrophoneEnabled = usePcpLiveLessonContext(s => s.isLocalPreviewMicrophoneEnabled);
    const toggleLocalPreviewMicrophone = usePcpLiveLessonContext(s => s.toggleLocalPreviewMicrophone);

    const onToggleMic = useCallback(async () => {
        toggleLocalPreviewMicrophone();
    }, [toggleLocalPreviewMicrophone]);
    const { audio: microphonePermissionGranted, video: cameraPermissionGranted } = usePcpLiveLessonContext(devicePermissionSelector);

    const hasCameras = devicesButtonProps.cameras.length > 0;
    const hasMicrophones = devicesButtonProps.microphones.length > 0;
    const microphoneDisabled = !microphonePermissionGranted || !hasMicrophones;
    const cameraDisabled = !cameraPermissionGranted || !hasCameras;

    const themeV8 = useTheme() as Theme & CallingTheme;  //using value of "FluentThemeProvider" component
    const getControlBarButtonStyles = useMemo(() => controlBarButtonStyles(themeV8), [themeV8])

    const onMicrophoneIconOn = useCallback(() => <MicrophoneIconOn disabled={microphoneDisabled} />
        , [microphoneDisabled, themeV8]);
    const onMicrophoneIconOff = useCallback(() => <MicrophoneIconOff disabled={microphoneDisabled} />
        , [microphoneDisabled, themeV8]);
    const onCameraIconOn = useCallback(() => <CameraIconOn disabled={cameraDisabled} />
        , [cameraDisabled, themeV8]);
    const onCameraIconOff = useCallback(() => <CameraIconOff disabled={cameraDisabled} />
        , [cameraDisabled, themeV8]);

    return <div><ControlBar layout="horizontal" styles={controlBarStyles()}>
        <MicrophoneButton
            data-ui-id="call-composite-local-device-settings-microphone-button"
            checked={isLocalMicrophoneEnabled}
            onToggleMicrophone={onToggleMic}
            onRenderOnIcon={onMicrophoneIconOn}
            onRenderOffIcon={onMicrophoneIconOff}
            disabled={microphoneDisabled}
            // disable tooltip as it obscures list of devices on mobile
            strings={
                props.mobileView ? { tooltipOnContent: '', tooltipOffContent: '', tooltipDisabledContent: '' } : {}
            }
            styles={getControlBarButtonStyles}
        />
        <CameraButton
            data-ui-id="call-composite-local-device-settings-camera-button"
            {...cameraButtonProps}
            disabled={cameraDisabled}
            onRenderOnIcon={onCameraIconOn}
            onRenderOffIcon={onCameraIconOff}
            // disable tooltip as it obscures list of devices on mobile
            strings={
                props.mobileView
                    ? {
                        tooltipOnContent: '',
                        tooltipOffContent: '',
                        tooltipDisabledContent: '',
                        tooltipVideoLoadingContent: ''
                    }
                    : {}
            }
            styles={getControlBarButtonStyles}
        />
    </ControlBar></div>
}
